import { useEffect, useMemo, useState } from 'react';
import { useChangeTick } from '../lib/changes';
import { api, hoursLabel, slotLabel, type MyShift, type PhysicianSummary, type Site } from '../lib/api';
import { addDays, addMonths, dayLabel, isoToday, monthLabel, monthOf, weekdayShort } from '../lib/dates';

/**
 * A physician's published shifts, one month at a time, grouped by day.
 * Loads a wide window once (a month back, a year ahead) and pages through it
 * on the device; opens on the current month, or on the first month that has
 * a shift if the current one is empty.
 */
export function MyShifts({ physician, sites }: { physician: PhysicianSummary; sites: Site[] }) {
  const today = isoToday();
  const from = addDays(today, -31);
  const to = addDays(today, 366);
  const [shifts, setShifts] = useState<MyShift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState(monthOf(today));
  const changes = useChangeTick();

  useEffect(() => {
    setShifts(null);
    api
      .myShifts(physician.abbreviation, from, to)
      .then((rows) => {
        setShifts(rows);
        const current = monthOf(today);
        if (!rows.some((r) => monthOf(r.date) === current)) {
          const next = rows.map((r) => r.date).filter((d) => d >= today).sort()[0];
          if (next) setMonth(monthOf(next));
        }
      })
      .catch((e: Error) => setError(e.message));
  }, [physician.abbreviation]);
  useEffect(() => {
    if (changes) api.myShifts(physician.abbreviation, from, to).then(setShifts).catch((e: Error) => setError(e.message));
  }, [changes]);

  const siteName = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);
  const inMonth = useMemo(() => (shifts ?? []).filter((s) => monthOf(s.date) === month).sort(byDateThenStart), [shifts, month]);
  const byDay = useMemo(() => {
    const m = new Map<string, MyShift[]>();
    for (const s of inMonth) m.set(s.date, [...(m.get(s.date) ?? []), s]);
    return [...m.entries()];
  }, [inMonth]);
  const monthsWithShifts = useMemo(() => new Set((shifts ?? []).map((s) => monthOf(s.date))), [shifts]);

  if (error) return <p className="error">Couldn't load your shifts ({error}).</p>;

  return (
    <section>
      <div className="monthnav">
        <button type="button" onClick={() => setMonth((m) => addMonths(m, -1))} aria-label="Previous month">‹</button>
        <h1>{monthLabel(month)}</h1>
        <button type="button" onClick={() => setMonth((m) => addMonths(m, 1))} aria-label="Next month">›</button>
      </div>
      <p className="summary">
        {shifts === null ? 'Loading…' : inMonth.length === 0 ? 'No published shifts this month.' : `${inMonth.length} shift${inMonth.length === 1 ? '' : 's'} on ${byDay.length} day${byDay.length === 1 ? '' : 's'}`}
        {shifts !== null && inMonth.length === 0 && nextMonthWithShifts(month, monthsWithShifts) && (
          <> <button className="linklike" type="button" onClick={() => setMonth(nextMonthWithShifts(month, monthsWithShifts)!)}>Jump to {monthLabel(nextMonthWithShifts(month, monthsWithShifts)!)}</button></>
        )}
      </p>
      <ol className="days">
        {byDay.map(([date, rows]) => (
          <li key={date} className={`day${date === today ? ' today' : ''}`}>
            <div className="dayhead">
              <span className="weekday">{weekdayShort(date)}</span>
              <span className="date">{dayLabel(date).replace(/^\w+, /, '')}</span>
            </div>
            <ul className="shifts">
              {rows.map((s) => (
                <li key={s.id} className="shift">
                  <span className={`site site-${s.scheduleWeek.siteId}`}>{siteName.get(s.scheduleWeek.siteId) ?? s.scheduleWeek.siteId}</span>
                  <span className="slot">{slotLabel(s.positionSlot)}</span>
                  <span className="hours">{s.start && s.end ? hoursLabel(s.start, s.end, s.crossesMidnight) : 'on call'}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}

function byDateThenStart(a: MyShift, b: MyShift): number {
  return a.date.localeCompare(b.date) || (a.start ?? '~').localeCompare(b.start ?? '~');
}

function nextMonthWithShifts(month: string, months: Set<string>): string | null {
  const later = [...months].filter((m) => m > month).sort();
  return later[0] ?? null;
}
